import { format, isValid, parse } from 'date-fns';
import Calendar from 'mdi-material-ui/Calendar';
import { ChangeEventHandler, useEffect, useRef, useState } from 'react';
import { DayPicker, SelectSingleEventHandler } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import './CustomDateField.css';
import { usePopper } from 'react-popper';

import { FieldErrors, useFieldContext } from '../';
import { Label } from '../../Elements';

export type CustomDateFieldProps = {
	label: string;
	className?: string;
	placeholder?: string;
	disabled?: boolean;
	setFieldTouched?: () => void;
	datesDisabled?: Date[];
	fromDate?: Date;
	toDate?: Date;
	required?: boolean;
};

export const CustomDateFieldV2 = (props: CustomDateFieldProps) => {
	const {
		label,
		disabled = false,
		required = false,
		setFieldTouched = () => {},
		datesDisabled,
		fromDate,
		toDate,
	} = props;

	const field = useFieldContext<string>();
	const name = field.name;

	const [selected, setSelected] = useState<Date>();
	const [isPopperOpen, setIsPopperOpen] = useState(false);

	const popperRef = useRef<HTMLDivElement>(null);
	const buttonRef = useRef<HTMLButtonElement>(null);
	const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
	const dateFormat = 'y-MM-dd';
	const dateFormatPlaceholder = 'YYYY-MM-DD';

	const popper = usePopper(popperRef.current, popperElement, {
		placement: 'bottom-start',
	});

	useEffect(() => {
		if (!isPopperOpen) return;

		const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
			const target = event.target as Node;
			if (
				popperRef.current?.contains(target) ||
				popperElement?.contains(target) ||
				buttonRef.current?.contains(target)
			) {
				return;
			}
			closePopper();
		};

		const handleEscape = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				closePopper();
			}
		};

		document.addEventListener('mousedown', handleOutsideClick);
		document.addEventListener('touchstart', handleOutsideClick);
		document.addEventListener('keydown', handleEscape);

		return () => {
			document.removeEventListener('mousedown', handleOutsideClick);
			document.removeEventListener('touchstart', handleOutsideClick);
			document.removeEventListener('keydown', handleEscape);
		};
	}, [isPopperOpen, popperElement]);

	const closePopper = () => {
		setIsPopperOpen(false);
		buttonRef?.current?.focus();
		setTimeout(() => setFieldTouched());
	};

	const handleInputChange: ChangeEventHandler<HTMLInputElement> = (e) => {
		field.setValue(e.currentTarget.value);
		const date = parse(e.currentTarget.value, dateFormat, new Date());
		if (isValid(date)) {
			setSelected(date);
		} else {
			setSelected(undefined);
		}
	};

	const handleButtonClick = () => {
		setIsPopperOpen(true);
	};

	const handleDaySelect: SelectSingleEventHandler = (date: Date | undefined) => {
		setSelected(date);
		if (date) {
			field.setValue(format(date, dateFormat));
			closePopper();
		} else {
			field.setValue('');
		}
	};

	// hide the error messages until the field has been touched
	const hideErrors = !field.state.meta.isTouched;

	return (
		<div>
			<Label
				htmlFor={name}
				label={label}
				required={required}
				className='mb-1'
				disabled={disabled}
			/>
			<div ref={popperRef}>
				<div className='relative'>
					<input
						id={name}
						size={12}
						type='text'
						placeholder={dateFormatPlaceholder}
						value={field.state.value}
						onChange={handleInputChange}
						onClick={handleButtonClick}
						className='flex-1 min-w-0 block w-full h-11 p-3 text-base rounded-md border border-primaryBorder placeholder:text-base placeholder:disabledText focus:ring-primaryBrand focus:border-primaryBrand'
					/>
					<button
						ref={buttonRef}
						type='button'
						aria-label='Pick a date'
						onClick={handleButtonClick}
						className='absolute right-1 top-2'
					>
						<Calendar className='text-secondaryTextDark' />
					</button>
				</div>
			</div>
			{isPopperOpen && (
				<div
					tabIndex={-1}
					style={popper.styles.popper}
					className='dialog-sheet'
					{...popper.attributes.popper}
					ref={setPopperElement}
					role='dialog'
					aria-label='Day picker calendar'
				>
					<DayPicker
						initialFocus={isPopperOpen}
						mode='single'
						defaultMonth={selected}
						selected={selected}
						onSelect={handleDaySelect}
						weekStartsOn={1}
						disabled={datesDisabled}
						fromDate={fromDate}
						toDate={toDate}
					/>
				</div>
			)}
			<FieldErrors meta={field.state.meta} name={name} hideErrors={hideErrors} />
		</div>
	);
};
